import React, { useEffect, useMemo, useState } from 'react'
import { v4 as uuidv4 } from 'uuid';
import {
  Box,
  Button,
  Container,
  Divider,
  InputAdornment,
  Paper,
  TextField,
  Typography,
  Link,
  useTheme,
  IconButton,
  SvgIcon
} from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { Person, Lock, Code, DarkMode, LightMode } from '@mui/icons-material'
import { authenticateBusiness, authenticateUsers, confirmPasswordReset, confirmRegistrationBusiness, confirmRegistrationUser, forgotPassword, getAuthenticatedUser, resendConfirmationCodeBusiness, resendConfirmationCodeUsers, signInWithCognito, signInWithGoogle, signUpBusiness, signUpBusiness2 } from '../../lib/authenticate'
import { getUser, putUser, putUserNotificationSettings } from '../../lib/connector/userQueries'
import businesspool from '../../lib/pools/businesspool';
import { getUserPool } from '../../lib/pools/pools';
import { useToast } from '../../components/modals/ToastProvider/ToastProvider';
import PrivacyNotice from '../../components/PrivacyNotice';
import TermsAndConditions from '../../components/TermsAndConditions';
import ForgotPasswordModal from '../../components/modals/ForgotPasswordModal';
import SupportContactModal from '../../components/modals/SupportContactModal';
import logo from '../../static/logo_main_3124x512.png';
import logoDark from '../../static/logo_main_dark_3124x512.png';
import { getAuth } from '../../contexts/authStore';
import CreateBusinessForm from './CreateBusinessForm';
import { useThemeContext } from '../../contexts/ThemeManager/ThemeManager';
import { putInviteRequest } from '../../lib/connector/requestQueries';
import moment from 'moment';
import { createCustomer } from '../../lib/connector/paypalQueries';
import userpool from '../../lib/pools/userpool';


const initialFormData = {
  username: '',
  password: '',
  companyName: '',
  managerName: '',
  email: '',
  phone: '',
  confirmPassword: '',
  confirmationCode: '',
  termsAgreed: false
}

const GoogleIcon = () => (
  <SvgIcon viewBox="0 0 24 24" sx={{ width: 20, height: 20 }}>
    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        fill="#EA4335"
      />
    </svg>
  </SvgIcon>
);

const Login: React.FC = () => {
  const navigate = useNavigate()
  const toast = useToast();
  const theme = useTheme();
  const { isDarkMode, toggleTheme } = useThemeContext();

  const [mode, setMode] = useState('login');
  const [resendLinkVisible, setResendLinkVisible] = useState<Boolean>(true)
  const [formData, setFormData] = useState<any>(initialFormData)
  const [verifyTimer, setVerifyTimer] = useState<ReturnType<typeof setTimeout> | null>(null);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [isGoogleSigningIn, setIsGoogleSigningIn] = useState(false);

  useEffect(() => {
    getAuthenticatedUser()
      .then(() => {
        console.info('User is already authenticated, redirecting to dashboard.');
        navigate('/dashboard')})
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (mode === "confirmation") {
      setResendLinkVisible(false);
      setVerifyTimer(setTimeout(() => {
        setResendLinkVisible(true);
      }, 5 * 60 * 1000))
    } else {
      setResendLinkVisible(false);
    }
    return () => {
      if (verifyTimer) {
        clearTimeout(verifyTimer);
        setVerifyTimer(null);
      }
    };
  }, [mode]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let { name, value } = e.target
    if (e.target.type === 'checkbox') {
      let value = (e.target as HTMLInputElement).checked
      setFormData((prev: any) => ({ ...prev, "termsAgreed": value }))
    } else {
      setFormData((prev: any) => ({ ...prev, [name]: value }))
    }
  }

  const switchMode = (newMode: React.SetStateAction<string>) => {
    setMode(newMode);
    // Reset form when switching modes
    setFormData({
      username: '',
      password: '',
      companyName: '',
      managerName: '',
      email: '',
      phone: '',
      confirmPassword: '',
      confirmationCode: '',
      termsAgreed: false
    });
  };

  const passwordRules = useMemo(() => {
    const p = formData.password || '';
    return {
      length: p.length >= 8,
      uppercase: /[A-Z]/.test(p),
      lowercase: /[a-z]/.test(p),
      number: /[0-9]/.test(p),
      special: /[^A-Za-z0-9]/.test(p),
    };
  }, [formData.password]);

  const passwordValid = Object.values(passwordRules).every(Boolean);
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.username);
  const phoneValid = formData.phone.replace(/\D/g, '').length === 10;
  const usernameValid = emailValid || phoneValid;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    try {
      if (mode === 'login') {
        setIsSigningIn(true);
        try {
          if (formData.username.includes('@')) {
            await signInWithCognito(formData.username, formData.password);
            navigate('/dashboard');
          } else {
            await authenticateUsers(formData.username.replaceAll("(", "").replaceAll(")", "").replaceAll("-", "").replaceAll(" ", ""), formData.password)
          }
          await new Promise(resolve => setTimeout(resolve, 800));
          getUser(getUserPool()!.getCurrentUser()!.getUsername()).then((user) => {
            if (user[0]?.archived) {
              toast.showError('This account is pending deletion. Please contact support for help.', 'Account Archived');
              getUserPool()!.getCurrentUser()!.signOut();
              setIsSigningIn(false);
              return;
            }
            else {
              navigate('/dashboard')
            }
          })
        }
        catch (err: any) {
          setIsSigningIn(false);
          if (err.code === 'UserNotConfirmedException') {
            setMode('confirmation')
            try {
              if (formData.username.includes('@')) {
                await resendConfirmationCodeBusiness(formData.username);
              } else {
                await resendConfirmationCodeUsers("+1" + formData.username.replaceAll("(", "").replaceAll(")", "").replaceAll("-", "").replaceAll(" ", ""));
              }
              toast.showError(err.message, 'Login Failed');
            }
            catch (err: any) {
              if (err.code === 'LimitExceededException') {
                toast.showError('Attempt limit exceeded, please try again later.', 'Login Failed');
                setMode('login')
              }
            }
          }
          else if (err.code === 'NotAuthorizedException') {
            toast.showError("Incorrect username or password.", 'Login Failed');
          }
        }

      } else if (mode === 'createBusiness') {
        try {
          if (formData.phone && formData.username === "") {
            await signUpBusiness2(
              formData.managerName,
              formData.phone,
              formData.password
            )
          } else {
            await signUpBusiness(
              formData.managerName,
              formData.username,
              formData.phone,
              formData.password
            )
          }
          setMode('confirmation')
        } catch (err: any) {
          toast.showError(err.message, 'Sign Up Failed');
        }
      } else if (mode === 'confirmation') {
        if (formData.username.includes('@')) {
          await confirmRegistrationBusiness(formData.confirmationCode, formData.username).then(async () => {
            await new Promise(resolve => setTimeout(resolve, 800));
          })
          await authenticateBusiness(formData.username, formData.password)
          putUser(businesspool.getCurrentUser()!.getUsername(), "owner", formData.managerName, formData.username, undefined, new Date().toISOString(), undefined, undefined, new Date().toISOString())
          putUserNotificationSettings(businesspool.getCurrentUser()!.getUsername(), false, true, false, false, false, false, true)
          createCustomer(businesspool.getCurrentUser()!.getUsername(), formData.managerName)
        } else {
          try {
            const userName = formData.username.replace(/\D/g, '').length !== 10 ? formData.phone.replaceAll("(", "").replaceAll(")", "").replaceAll("-", "").replaceAll(" ", "") : formData.username;
            await confirmRegistrationUser(formData.confirmationCode, "+1" + userName).then(async (response) => {
              await new Promise(resolve => setTimeout(resolve, 800));
            })
            if (formData.username.replace(/\D/g, '').length !== 10) {
                await authenticateUsers(userName, formData.password)
                await putUser(userpool.getCurrentUser()!.getUsername(), "owner", formData.managerName, formData.username, undefined, new Date().toISOString(), undefined, undefined, new Date().toISOString())
                await putUserNotificationSettings(userpool.getCurrentUser()!.getUsername(), false, true, false, false, false, false, true)
                await createCustomer(userpool.getCurrentUser()!.getUsername(), formData.managerName)
            } else {
              const userType = getAuth().requests;
              const scheduleId = userType?.split("#")[1] || "";
              try {
                await authenticateUsers(userName, formData.password)
                await putInviteRequest(scheduleId, uuidv4(), "JOIN", getUserPool()!.getCurrentUser()!.getUsername(),
                  formData.managerName, moment().format("YYYY-MM-DDTHH:mm:ss.sssZ"), JSON.stringify({
                    userId: getUserPool()!.getCurrentUser()!.getUsername(),
                    joinDate: moment().format("YYYY-MM-DDTHH:mm:ss.sssZ"),
                  }), 'pending')
                await putUser(getAuth().userId || "", "employee", getAuth().name || "", "", userName, new Date().toISOString(), undefined, undefined, new Date().toISOString())
              } catch (err: any) {
                toast.showError(err.message, 'Confirmation Failed');
              }
            }
            await new Promise(resolve => setTimeout(resolve, 1000));
            navigate('/dashboard');
          } catch (err: any) {
            toast.showError(err.message, 'Confirmation Failed');
          }
        }
        navigate('/dashboard')
      }
    } catch (err) {
      toast.showError('Login failed');
    }
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.paper} 50%, ${theme.palette.background.paper} 100%)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2
      }}
    >
      <Container maxWidth="sm">
        {/* Logo and Header */}
        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <img
            src={isDarkMode ? logoDark : logo}
            onClick={(() => {
              navigate('/')
            })}
            alt="Stafflows Logo"
            style={{
              width: "80%",
              cursor: 'pointer'
            }}
          />
        </Box>

        {/* Login/Create Business Card */}
        <Paper elevation={6} sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <Box component="form" onSubmit={handleSubmit} sx={{ p: 4 }}>

            {/* LOGIN MODE */}
            {mode === 'login' && (
              <>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h5" fontWeight="bold" gutterBottom>
                    Welcome Back
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Sign in to access your account
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                  {/* Username Field */}
                  <TextField
                    fullWidth
                    label="Username"
                    name="username"
                    value={formData.username}
                    onChange={handleChange}
                    variant="outlined"
                    placeholder="Enter your username"
                    helperText="Use the email or phone number you signed up with"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Person color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />

                  {/* Password Field */}
                  <TextField
                    fullWidth
                    label="Password"
                    name="password"
                    type="password"
                    value={formData.password}
                    onChange={handleChange}
                    variant="outlined"
                    placeholder="••••••••"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />

                  {/* Forgot Password */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Link
                      component="button"
                      type="button"
                      onClick={() => setShowForgotPassword(true)}
                      variant="body2"
                      underline="hover"
                      sx={{ fontWeight: 600 }}
                    >
                      Forgot password?
                    </Link>
                  </Box>

                  {/* Login Button */}
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    disabled={
                      isSigningIn ||
                      !formData.username ||
                      !formData.password ||
                      formData.password.length < 8 ||
                      !(/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.username) || /^\d{10}$/.test(formData.username.replace(/\D/g, '')))
                    }
                    sx={{
                      py: 1.5,
                      textTransform: 'none',
                      fontSize: '1rem',
                      fontWeight: 600,
                      boxShadow: 3
                    }}
                  >
                    {isSigningIn ? 'Signing in...' : 'Sign In'}
                  </Button>
                  <Button
                    fullWidth
                    variant="outlined"
                    size="large"
                    startIcon={<GoogleIcon />}
                    disabled={isGoogleSigningIn}
                    onClick={() => { setIsGoogleSigningIn(true); signInWithGoogle(); }}
                    sx={{
                      py: 1.5,
                      textTransform: 'none',
                      fontSize: '1rem',
                      fontWeight: 600
                    }}
                  >
                    {isGoogleSigningIn ? 'Redirecting...' : 'Sign In with Google'}
                  </Button>

                  {/* Switch to Create Business */}
                  <Divider sx={{ my: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      OR
                    </Typography>
                  </Divider>

                  <Button
                    fullWidth
                    variant="outlined"
                    size="large"
                    onClick={() => switchMode('createBusiness')}
                    sx={{
                      py: 1.5,
                      textTransform: 'none',
                      fontSize: '1rem',
                      fontWeight: 600
                    }}
                  >
                    Create Account
                  </Button>
                </Box>
              </>
            )}

            {/* CREATE BUSINESS MODE */}
            {mode === 'createBusiness' && (
              <CreateBusinessForm
                formData={formData}
                passwordRules={passwordRules}
                passwordValid={passwordValid}
                usernameValid={usernameValid}
                emailValid={emailValid}
                phoneValid={phoneValid}
                handleChange={handleChange}
                switchMode={switchMode}
              />
            )}

            {/* CONFIRMATION MODE */}
            {mode === 'confirmation' && (
              <>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h5" fontWeight="bold" gutterBottom>
                    Please Confirm your Account to Proceed
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    We {formData.username.includes('@') ? 'emailed' : 'texted'} you a code, enter it below to confirm your account
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                  {/* Username Field */}
                  <TextField
                    fullWidth
                    label="Confirmation Code"
                    name="confirmationCode"
                    value={formData.confirmationCode}
                    onChange={handleChange}
                    variant="outlined"
                    placeholder="Enter your code"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Code color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />

                  {/* Confirmation */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Button variant='text' sx={{ fontWeight: 600 }} onClick={() => {
                      if (resendLinkVisible) {
                        try {
                          if (formData.username.includes('@')) {
                            resendConfirmationCodeBusiness(formData.username);
                          } else {
                            resendConfirmationCodeUsers("+1" + formData.username);
                          }
                        } catch (err: any) {
                          if (err.code === 'LimitExceededException') {
                            toast.showError('Attempt limit exceeded, please try again later.', 'Login Failed');
                          }
                        }
                      }
                      setResendLinkVisible(false);
                    }}>
                      {resendLinkVisible ? "Need a new code, click here." : `${formData.username.includes('@') ? 'Email' : 'SMS'} sent. Please check your ${formData.username.includes('@') ? 'inbox' : 'phone'}.`}
                    </Button>
                  </Box>

                  {/* Login Button */}
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    onClick={((e: React.MouseEvent<HTMLButtonElement>) => {
                      e.preventDefault();
                      handleSubmit(e as any);
                    })}
                    sx={{
                      py: 1.5,
                      textTransform: 'none',
                      fontSize: '1rem',
                      fontWeight: 600,
                      boxShadow: 3
                    }}
                  >
                    Confirm
                  </Button>

                </Box>
              </>
            )}

          </Box>
        </Paper>

        {/* Help Section */}
        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Need help?{' '}
            <Link component="button" type="button" onClick={() => setShowSupportModal(true)} underline="hover" sx={{ fontWeight: 600 }}>
              Contact support
            </Link>
          </Typography>
        </Box>

        {/* Footer */}
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mt: 0.5 }}>
            <PrivacyNotice color={'primary'} />
            <Typography variant="caption" color="text.secondary">•</Typography>
            <TermsAndConditions color={'primary'} />
            <Typography variant="caption" color="text.secondary">•</Typography>
            <IconButton size='small' color={isDarkMode ? 'info' : 'primary'} onClick={toggleTheme}>
              {isDarkMode ? <LightMode /> : <DarkMode />}
            </IconButton>
          </Box>
        </Box>
      </Container>

      <ForgotPasswordModal
        open={showForgotPassword}
        onClose={() => setShowForgotPassword(false)}
        onSendCode={async (username: any) => {
          await forgotPassword(username.replaceAll("(", "").replaceAll(")", "").replaceAll("-", "").replaceAll(" ", ""), username.includes("@"));
        }}
        onResetPassword={async (username: any, code: any, newPassword: any) => {
          await confirmPasswordReset(username.replaceAll("(", "").replaceAll(")", "").replaceAll("-", "").replaceAll(" ", ""), code, newPassword, username.includes("@"));
        }}
      />

      <SupportContactModal open={showSupportModal} onClose={() => setShowSupportModal(false)} />
    </Box>
  );
}

export default Login